import React, { useId, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, FormControl,
  IconButton, InputLabel, MenuItem, Select, Tab, Tabs, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Action, colors, fonts, ImagePlaceholder, PublicImage } from './design';
import {
  DemoId, DemoLocale, DemoObservation, DemoState, demoDisclaimer, demoIds,
  demoObservations, demoProperties, demoSearch, demoYearHistory, demoYears, parseDemoState, toggleResolved,
} from './demoData';
import { publicPath } from './routes';

interface ObservationImageProps {
  items: DemoObservation[];
  selected: DemoId;
  onSelect: (id: DemoId) => void;
  schematic?: boolean;
  eager?: boolean;
  fullResolution?: boolean;
  squareMarkers?: boolean;
  locale?: DemoLocale;
  background?: React.ReactNode;
  locationLabel?: string;
  compactMarkers?: boolean;
}

const copy = {
  sv: {
    schematicAria: 'Schematisk lägesvy, inte en uppmätt 3D-modell',
    imageFallback: 'Fasadbilden kunde inte laddas. Observationerna finns i listan.',
    imageAlt: 'KlaraViews miljöfoto med illustrativa observationer',
    facade: 'FASAD A',
    schematicView: 'SCHEMATISK VY',
    illustratedView: 'ILLUSTRERAD VY',
    show: 'Visa',
    title: 'Byggnadspasset',
    demo: 'INTERAKTIV DEMO',
    report: 'Exempelrapport',
    homepageDisclaimer: 'Illustrativ demo med exempeldata, inte en skadeanalys av byggnaden. Beslut kräver rätt kompetens.',
    changes: 'Ändringar gäller endast denna demo och återställs vid byte av fastighet eller år.',
    invalid: 'Ogiltiga demoval i länken har ersatts med standardval.',
    property: 'Exempelfastighet',
    inspectionYear: 'Inspektionsår',
    reset: 'Återställ demonstrationen',
    filter: 'Filtrera observationer',
    all: 'Alla observationer',
    priorities: 'Prioriterade',
    facadeView: 'Fasadvisning',
    photo: 'Fasadbild',
    location: 'Lägesvy',
    enlarge: 'Förstora fasadbilden',
    schematicNote: 'Schematisk orientering, inte en uppmätt 3D-modell.',
    photoNote: 'Miljöfoto. Observationernas placering är illustrativ.',
    observations: 'Observationer',
    count: (visible: number, total: number) => visible === total
      ? `${total} ${total === 1 ? 'observation' : 'observationer'} att titta på`
      : `${visible} av ${total} observationer`,
    checkFirst: 'Kontrollera först',
    whatWeSee: 'Vad vi ser',
    whatNext: 'Vad gör jag nu?',
    details: 'Historik och detaljer',
    historyLabel: 'Historik i exemplet',
    aiLabel: 'AI-STÖD / EXEMPEL',
    aiText: 'En möjlig avvikelse har markerats för granskning. Markeringen är ingen teknisk diagnos.',
    assessmentLabel: 'MÄNSKLIG BEDÖMNING / EXEMPEL',
    history: demoYearHistory.sv,
    currentStatus: 'Aktuell status',
    historyNote: 'Årtalen visar ett exempel, inte en verklig inspektionshistorik.',
    resolvedNext: 'Dokumentera åtgärden och följ upp vid nästa överenskomna inspektion.',
    caution: 'En observation är inte en diagnos. Beslut kräver rätt kompetens.',
    undoResolved: 'Ångra åtgärdad · demo',
    markResolved: 'Markera åtgärdad · demo',
    notSaved: 'Sparas inte i någon fastighet eller kundrapport.',
    emptyTitle: 'Inga prioriterade observationer',
    emptyText: 'Inga observationer matchar filtret i detta exempel. Det är ingen garanti för byggnadens skick.',
    showAll: 'Visa alla observationer',
    technical: 'Tekniska detaljer',
    technicalText: (property: string, year: number) => `${property} / Fasad A / Bildreferens DEMO-A / Exempelår ${year}. ID, bildreferens och bedömning hålls samman. Inga mätvärden, GPS-positioner eller verifierade diagnoser ingår.`,
    overviewText: 'Samma byggnad. Mer kunskap för varje inspektion. Jämför bild, observation och dokumenterad åtgärd.',
    dialogTitle: 'Fasad A · illustrativ demo',
    close: 'Stäng fullskärm',
    keyboard: 'Använd vänster/höger pil för att välja observation och Escape för att stänga.',
  },
  en: {
    schematicAria: 'Schematic location view, not a measured 3D model',
    imageFallback: 'The façade image could not be loaded. The observations remain available in the list.',
    imageAlt: 'KlaraView setting photograph with illustrative observations',
    facade: 'FAÇADE A',
    schematicView: 'SCHEMATIC VIEW',
    illustratedView: 'ILLUSTRATED VIEW',
    show: 'Show',
    title: 'Building pass',
    demo: 'INTERACTIVE DEMO',
    report: 'Sample report',
    homepageDisclaimer: 'Illustrative demo using sample data, not a damage assessment of the building. Decisions require the right expertise.',
    changes: 'Changes apply only to this demo and reset when you change property or year.',
    invalid: 'Invalid demo choices in the link were replaced with safe defaults.',
    property: 'Sample property',
    inspectionYear: 'Inspection year',
    reset: 'Reset the demonstration',
    filter: 'Filter observations',
    all: 'All observations',
    priorities: 'Prioritised',
    facadeView: 'Façade view',
    photo: 'Façade image',
    location: 'Location view',
    enlarge: 'Enlarge the façade image',
    schematicNote: 'Schematic orientation, not a measured 3D model.',
    photoNote: 'Setting photograph. Observation positions are illustrative.',
    observations: 'Observations',
    count: (visible: number, total: number) => visible === total
      ? `${total} ${total === 1 ? 'observation' : 'observations'} to review`
      : `${visible} of ${total} observations`,
    checkFirst: 'Check first',
    whatWeSee: 'What we see',
    whatNext: 'What do I do now?',
    details: 'History and details',
    historyLabel: 'History in the example',
    aiLabel: 'AI SUPPORT / EXAMPLE',
    aiText: 'A possible deviation has been marked for review. The marker is not a technical diagnosis.',
    assessmentLabel: 'PROFESSIONAL ASSESSMENT / EXAMPLE',
    history: demoYearHistory.en,
    currentStatus: 'Current status',
    historyNote: 'The years illustrate an example, not a real inspection history.',
    resolvedNext: 'Document the action and follow up at the next agreed inspection.',
    caution: 'An observation is not a diagnosis. Decisions require the right expertise.',
    undoResolved: 'Undo resolved · demo',
    markResolved: 'Mark as resolved · demo',
    notSaved: 'Nothing is saved to a property or customer report.',
    emptyTitle: 'No prioritised observations',
    emptyText: 'No observations match the filter in this example. This is not a guarantee of the building condition.',
    showAll: 'Show all observations',
    technical: 'Technical details',
    technicalText: (property: string, year: number) => `${property} / Façade A / Image reference DEMO-A / Example year ${year}. ID, image reference and assessment stay connected. No measurements, GPS positions or verified diagnoses are included.`,
    overviewText: 'The same building. More knowledge with each inspection. Compare images, observations and documented actions.',
    dialogTitle: 'Façade A · illustrative demo',
    close: 'Close full screen',
    keyboard: 'Use the left and right arrow keys to select an observation and Escape to close.',
  },
};

export function ObservationPriorityBadge({ locale, id }: { locale: DemoLocale; id?: string }) {
  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: colors.graphite,
    color: colors.white, px: 1.5, py: .8, fontSize: 11 }}>
    <Box component="span" aria-hidden="true" sx={{ bgcolor: colors.rustDark, px: .75, fontWeight: 700 }}>!</Box>
    <Box component="span" id={id}>{copy[locale].checkFirst}</Box>
  </Box>;
}

export function ObservationPriorityIndicator({ compact = false }: { compact?: boolean }) {
  return <Box component="span" aria-hidden="true" sx={{
    position: 'absolute', top: -7, right: -5, width: 18, height: 18,
    display: compact ? { xs: 'none', sm: 'grid' } : 'grid', placeItems: 'center', borderRadius: '50%',
    bgcolor: colors.rustDark, border: `1px solid ${colors.white}`, fontSize: 11,
  }}>!</Box>;
}

export function ObservationImage({
  items, selected, onSelect, schematic = false, eager = false, fullResolution = false, squareMarkers = false, locale = 'sv',
  background, locationLabel, compactMarkers = false,
}: ObservationImageProps) {
  const [failed, setFailed] = useState(false);
  const priorityDescriptionId = useId();
  const text = copy[locale];
  return <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', bgcolor: colors.soft, overflow: 'hidden' }}>
    {background !== undefined ? background : schematic ? <Box component="svg" role="img" aria-label={text.schematicAria} viewBox="0 0 400 300" sx={{ width: '100%', height: '100%', fill: 'none', stroke: '#7C8779' }}>
      <path d="M40 260V75L245 25l110 45v190H40M245 25v235M40 75l205 45 110-50M245 120v140" />
      {[0, 1, 2].map(row => [0, 1, 2, 3].map(col => <rect key={`${row}-${col}`} x={65 + col * 43} y={120 + row * 42} width="20" height="25" />))}
    </Box> : failed ? <ImagePlaceholder label={text.imageFallback} /> :
      <PublicImage image="facade" alt={text.imageAlt} eager={eager} fullResolution={fullResolution} onError={() => setFailed(true)}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '35% 65%', display: 'block', filter: 'saturate(.72)' }} />}
    <Box sx={{ position: 'absolute', left: 12, right: 12, top: 12, display: 'flex', flexWrap: 'wrap', gap: 1, pointerEvents: 'none' }}>
      <Box sx={{ bgcolor: colors.graphite, color: colors.white, px: 1.5, py: .8, fontFamily: fonts.mono, fontSize: 11 }}>
        {locationLabel || text.facade} / {schematic ? text.schematicView : text.illustratedView}
      </Box>
      {items.some(item => item.priority) && <ObservationPriorityBadge locale={locale} id={priorityDescriptionId} />}
    </Box>
    {items.map(item => <Button key={item.id} aria-label={`${text.show} ${item.id}: ${item.title}`} aria-pressed={selected === item.id}
      aria-describedby={item.priority ? priorityDescriptionId : undefined}
      onClick={() => onSelect(item.id)} sx={{
        position: 'absolute', left: `${item.x}%`, top: `${item.y}%`, transform: 'translate(-50%, -50%)',
        minWidth: compactMarkers ? { xs: 24, sm: 44 } : 44,
        minHeight: compactMarkers ? { xs: 24, sm: 44 } : 44,
        width: compactMarkers ? { xs: 24, sm: 44 } : 44,
        height: compactMarkers ? { xs: 24, sm: 44 } : 44,
        fontSize: compactMarkers ? { xs: 10, sm: 14 } : undefined,
        p: 0, border: '2px solid white', borderRadius: squareMarkers ? 0 : '50%',
        color: colors.white, bgcolor: item.priority ? colors.rustDark : colors.graphite,
        boxShadow: selected === item.id
          ? `0 0 0 3px ${colors.paper}, 0 0 0 5px ${colors.ink}`
          : '0 0 0 3px rgba(255,255,255,.18)',
        '&:hover': { bgcolor: item.priority ? colors.rust : colors.muted },
      }}>
      {item.id.slice(-2)}
      {item.priority && <ObservationPriorityIndicator compact={compactMarkers} />}
    </Button>)}
  </Box>;
}

export function BuildingDemo({ search = '', locale = 'sv', homepage = false }: { search?: string; locale?: DemoLocale; homepage?: boolean }) {
  const parsed = parseDemoState(search);
  const [state, setState] = useState<DemoState>(homepage ? parseDemoState('').state : parsed.state);
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<DemoId>('F-001');
  const [schematic, setSchematic] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(new URLSearchParams(search).get('detail') === 'technical');
  const [fullscreen, setFullscreen] = useState(false);
  const detailsToggleRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const text = copy[locale];
  const items = demoObservations(state, locale);
  const visible = items.filter(item => !priorityOnly || item.priority);
  const selected = visible.find(item => item.id === selectedId) || visible[0];
  const select = (id: DemoId) => setSelectedId(id);
  const changeContext = (property: number, year: DemoState['year']) => {
    setState({ property, year, resolved: [], reopened: [] });
    setSelectedId('F-001');
  };
  const reset = () => {
    setState({ property: 0, year: 2026, resolved: [], reopened: [] });
    setPriorityOnly(false); setSelectedId('F-001'); setSchematic(false); setDetailsOpen(false);
    detailsToggleRef.current?.focus();
  };
  const rememberReportSource: React.MouseEventHandler<HTMLAnchorElement> = event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const current = new URL(window.location.href);
    const snapshot = new URLSearchParams(demoSearch(state));
    ['property', 'year', 'resolved', 'reopened'].forEach(key => {
      const value = snapshot.get(key);
      if (value === null) current.searchParams.delete(key);
      else current.searchParams.set(key, value);
    });
    if (current.pathname === publicPath('home', locale)) current.hash = 'prova';
    window.history.replaceState(window.history.state, '', `${current.pathname}${current.search}${current.hash}`);
  };
  return <Box sx={{ bgcolor: colors.white, border: `1px solid ${colors.line}`, minWidth: 0 }}>
    <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${colors.line}` }}>
      <Box sx={{ mr: 'auto', minWidth: 0 }}>
        <Typography sx={{ fontFamily: fonts.mono, color: colors.muted, fontSize: 11, mb: 1 }}>{text.title} / {text.demo}</Typography>
        <Typography sx={{ fontSize: { xs: 23, md: 28 }, fontWeight: 700, lineHeight: 1.2 }}>{text.count(visible.length, items.length)}</Typography>
      </Box>
      <Action href={`${publicPath('exampleReport', locale)}${demoSearch(state)}`} secondary onClick={rememberReportSource}>{text.report}</Action>
    </Box>
    <Box role="note" sx={{ p: 2, bgcolor: colors.soft, fontSize: 13 }}>{homepage ? text.homepageDisclaimer : demoDisclaimer[locale]}</Box>
    {parsed.invalid && <Alert severity="warning">{text.invalid}</Alert>}
    {!homepage && <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 180, maxWidth: '100%' }}>
        <InputLabel id={`${uid}-property`}>{text.property}</InputLabel>
        <Select labelId={`${uid}-property`} label={text.property} value={state.property} onChange={event => changeContext(Number(event.target.value), state.year)}>
          {demoProperties[locale].map((name, index) => <MenuItem key={name} value={index}>{name}</MenuItem>)}
        </Select>
      </FormControl>
      <Tabs value={state.year} onChange={(_, year: DemoState['year']) => changeContext(state.property, year)} aria-label={text.inspectionYear} variant="scrollable" scrollButtons="auto" sx={{ minWidth: 0 }}>
        {demoYears.map(year => <Tab key={year} value={year} label={String(year)} />)}
      </Tabs>
      <Tabs value={priorityOnly ? 1 : 0} onChange={(_, value) => setPriorityOnly(value === 1)} aria-label={text.filter}
        variant="scrollable" scrollButtons="auto" sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Tab label={`${text.all} · ${items.length}`} /><Tab label={`${text.priorities} · ${items.filter(item => item.priority).length}`} />
      </Tabs>
    </Box>}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, borderTop: `1px solid ${colors.line}` }}>
      <Box sx={{ minWidth: 0, p: { xs: 1.5, md: 3 }, bgcolor: colors.paper }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!homepage && <Tabs value={schematic ? 1 : 0} onChange={(_, value) => setSchematic(value === 1)} aria-label={text.facadeView}>
            <Tab label={text.photo} /><Tab label={text.location} />
          </Tabs>}
          <IconButton aria-label={text.enlarge} disabled={!selected} onClick={() => setFullscreen(true)} sx={{ ml: 'auto' }}><OpenInFullIcon /></IconButton>
        </Box>
        <ObservationImage items={visible} selected={selected?.id || selectedId} onSelect={select} schematic={schematic} locale={locale} />
        {!homepage && <Typography sx={{ fontSize: 11, color: colors.muted, my: 1.5 }}>{schematic ? text.schematicNote : text.photoNote}</Typography>}
        <Box role="group" aria-label={text.observations} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1, mt: homepage ? 1.5 : 0 }}>
          {visible.map(item => <Button key={item.id} aria-pressed={selected?.id === item.id} onClick={() => select(item.id)}
            sx={{
              display: 'block', textAlign: 'left', minWidth: 0, px: 1.5, py: 1.5,
              color: colors.ink, bgcolor: selected?.id === item.id ? colors.soft : colors.white,
              border: `1px solid ${selected?.id === item.id ? colors.ink : colors.line}`,
              boxShadow: selected?.id === item.id ? `inset 0 0 0 1px ${colors.ink}` : 'none',
            }}>
            <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: .75, alignItems: 'baseline', mb: .75 }}>
              <Box component="span" sx={{ fontFamily: fonts.mono, fontSize: 10 }}>{item.id}</Box>
              {item.priority && <Box component="span" sx={{ color: colors.rustDark, fontSize: 10 }}>{text.checkFirst}</Box>}
            </Box>
            <Box component="span" sx={{ display: 'block', fontSize: 13 }}>{item.title}</Box>
          </Button>)}
        </Box>
      </Box>
      <Box sx={{ p: { xs: 2.5, md: 4 }, minWidth: 0 }}>
        {selected ? <>
          <Typography sx={{ fontFamily: fonts.mono, fontSize: 12, mb: 2 }}>{selected.id} / {text.facade}{!homepage && ` / ${state.year}`}</Typography>
          <Typography variant="h3" sx={{ fontSize: 27, mb: 2 }}>{selected.title}</Typography>
          {(!homepage || selected.priority) && <Chip label={selected.priority ? text.checkFirst : selected.status} size="small" sx={{ color: selected.priority ? colors.rustDark : colors.green, bgcolor: colors.soft, mb: 3 }} />}
          <Box component="section" aria-labelledby={`${uid}-observation`} sx={{ borderTop: `1px solid ${colors.line}`, py: 2.5 }}>
            <Typography component="h4" id={`${uid}-observation`} sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{text.whatWeSee}</Typography>
            <Typography sx={{ color: colors.muted }}>{selected.assessment}</Typography>
          </Box>
          <Box component="section" aria-labelledby={`${uid}-next`} sx={{ borderLeft: `2px solid ${colors.rust}`, bgcolor: colors.paper, p: 2.5, mb: 2.5 }}>
            <Typography component="h4" id={`${uid}-next`} sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{text.whatNext}</Typography>
            <Typography>{selected.resolved ? text.resolvedNext : selected.next}</Typography>
          </Box>
        </> : <Box role="status" sx={{ py: 5 }}><Typography variant="h3" sx={{ fontSize: 25 }}>{text.emptyTitle}</Typography>
          <Typography sx={{ my: 2 }}>{text.emptyText}</Typography>
          <Button onClick={() => setPriorityOnly(false)}>{text.showAll}</Button></Box>}
        {!homepage && <>
        <Typography sx={{ fontSize: 12, color: colors.muted, mb: 2.5 }}>{text.caution}</Typography>
        <Accordion expanded={detailsOpen} onChange={(_, expanded) => setDetailsOpen(expanded)}
          disableGutters elevation={0} square TransitionProps={{ unmountOnExit: true, timeout: 0 }}
          sx={{ bgcolor: 'transparent', borderTop: `1px solid ${colors.line}`, '&:before': { display: 'none' } }}>
          <AccordionSummary ref={detailsToggleRef} id={`${uid}-details-toggle`} aria-controls={`${uid}-details`}
            expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 48 }}>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: 14 }}>{text.details}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 1 }}>
            <Typography component="h4" sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{text.historyLabel}</Typography>
            <Typography sx={{ fontSize: 14 }}>{text.history[state.year]}</Typography>
            {selected && <Typography sx={{ fontSize: 14, mt: 1 }}>{text.currentStatus}: {selected.status}.</Typography>}
            <Typography sx={{ color: colors.muted, fontSize: 12, mt: 1 }}>{text.historyNote} {text.overviewText}</Typography>
            {selected && <Box sx={{ borderTop: `1px solid ${colors.line}`, mt: 2.5, pt: 2.5 }}>
              <Typography sx={{ fontFamily: fonts.mono, fontSize: 11, mb: 1 }}>{text.aiLabel}</Typography>
              <Typography sx={{ fontSize: 14, mb: 2 }}>{text.aiText}</Typography>
              <Typography sx={{ fontFamily: fonts.mono, fontSize: 11, mb: 1 }}>{text.assessmentLabel}</Typography>
              <Typography sx={{ fontSize: 14 }}>{selected.assessment}</Typography>
            </Box>}
            <Box sx={{ borderTop: `1px solid ${colors.line}`, mt: 2.5, pt: 2.5 }}>
              <Typography component="h4" sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{text.technical}</Typography>
              <Typography sx={{ fontSize: 12, color: colors.muted }}>{text.technicalText(demoProperties[locale][state.property], state.year)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5 }}>
              {selected && <Button variant="outlined" onClick={() => {
                if (priorityOnly && !selected.resolved) detailsToggleRef.current?.focus();
                setState(toggleResolved(state, selected.id));
              }}>{selected.resolved ? text.undoResolved : text.markResolved}</Button>}
              <Button startIcon={<RestartAltIcon />} onClick={reset}>{text.reset}</Button>
            </Box>
            <Typography sx={{ fontSize: 12, color: colors.muted, mt: 1.5 }}>{text.notSaved} {text.changes}</Typography>
          </AccordionDetails>
        </Accordion>
        </>}
      </Box>
    </Box>
    <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullWidth maxWidth="lg" aria-labelledby={`${uid}-fullscreen`}
      onKeyDown={event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !selected || !visible.length) return;
        event.preventDefault();
        const index = visible.findIndex(item => item.id === selected.id);
        select(visible[(index + (event.key === 'ArrowRight' ? 1 : visible.length - 1)) % visible.length].id);
      }}>
      <DialogTitle id={`${uid}-fullscreen`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>{text.dialogTitle}
        <IconButton aria-label={text.close} onClick={() => setFullscreen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent><ObservationImage items={visible} selected={selected?.id || demoIds[0]} onSelect={select} schematic={schematic} locale={locale} fullResolution />
        <Typography sx={{ mt: 2 }}>{selected?.id} · {selected?.title}. {text.keyboard}</Typography></DialogContent>
    </Dialog>
  </Box>;
}
